/**
 * Homeowner Projects Page
 * Exact recreation from c34a46ad-86fa-438c-bdc0-955a58a592f9.javascript
 * Wired to getProjects() API
 */

import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import projectPlaceholder from "@/assets/images/project-placeholder.svg";
import { SkeletonRows } from "@/components/ui/skeleton";
import { queryKeys } from "@/lib/query-keys";
import {
	Btn,
	Chip,
	Eyebrow,
	hoStyles,
	Icn,
	ItalicAccent,
} from "../components/homeowner/design-primitives";
import { HoProjectCard } from "../components/homeowner/ho-project-card";
import type { AvatarTone, HoProject, StageTone } from "../data/mock-homeowner";
import { useProjects } from "../hooks/use-projects";
import {
	getDesignerById,
	type Project,
	setPrimaryProject,
} from "../services/projects-api.service";

type FilterTab = "all" | "active" | "awaiting" | "done";

const FILTERS: [FilterTab, string][] = [
	["all", "All"],
	["active", "In progress"],
	["awaiting", "Seeking designer"],
	["done", "Completed"],
];

const AVATAR_TONES: AvatarTone[] = ["ink", "amber", "stone", "cognac", "terra"];

function getRandomAvatarTone(): AvatarTone {
	return AVATAR_TONES[Math.floor(Math.random() * AVATAR_TONES.length)];
}

function getStageInfo(status: Project["status"]): {
	stage: string;
	stageTone: StageTone;
	pct: number;
} {
	switch (status) {
		case "completed":
			return { stage: "Completed", stageTone: "done", pct: 100 };
		case "in_progress":
			return { stage: "In Progress", stageTone: "active", pct: 50 };
		case "seeking_designer":
			return { stage: "Seeking designer", stageTone: "awaiting", pct: 0 };
		case "awaiting_funding":
			return { stage: "Awaiting funding", stageTone: "risk", pct: 0 };
		case "draft":
			return { stage: "Draft", stageTone: "neutral", pct: 0 };
		case "cancelled":
			return { stage: "Cancelled", stageTone: "risk", pct: 0 };
		default:
			return { stage: "Unknown", stageTone: "neutral", pct: 0 };
	}
}

async function transformProject(apiProject: Project): Promise<HoProject> {
	const stageInfo = getStageInfo(apiProject.status);
	const createdAt = new Date(apiProject.createdAt);
	const now = new Date();
	const daysSinceCreated = Math.floor(
		(now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24),
	);
	const totalDays = (apiProject.timelineWeeks || 12) * 7;

	let designerName: string | null = null;
	let designerIni: string | null = null;
	let designerTone: AvatarTone | null = null;
	let studio: string | null = null;

	if (apiProject.designerId) {
		try {
			const designer = await getDesignerById(apiProject.designerId);
			if (designer) {
				designerName = designer.name;
				designerIni = designer.name
					.split(" ")
					.map((n) => n[0])
					.join("")
					.toUpperCase()
					.slice(0, 2);
				designerTone = getRandomAvatarTone();
				studio = designer.firmName || null;
			}
		} catch (err) {
			console.error(
				`Failed to fetch designer ${apiProject.designerId} for project ${apiProject.id}:`,
				err,
			);
		}
	}

	const propertyRooms = apiProject.property?.rooms;
	const sizeSqft = apiProject.property?.sizeSqft;
	let configStr = "";
	if (propertyRooms?.bedrooms) {
		configStr = `${propertyRooms.bedrooms}BHK`;
		if (sizeSqft) {
			configStr += ` · ${sizeSqft}sqft`;
		} else if (propertyRooms.bathrooms) {
			configStr += ` · ${propertyRooms.bathrooms} Bath`;
		}
	} else if (sizeSqft) {
		configStr = `${sizeSqft} sqft`;
	} else if (apiProject.scope === "full_home") {
		configStr = "Full home";
	} else {
		configStr = "Partial";
	}

	const loc = apiProject.property?.locality
		? apiProject.property.city
			? `${apiProject.property.locality}, ${apiProject.property.city}`
			: apiProject.property.locality
		: apiProject.property?.city
			? apiProject.property.city
			: apiProject.property?.sizeSqft
				? `${apiProject.property.sizeSqft} sqft`
				: "Location TBD";

	return {
		id: apiProject.id,
		name: apiProject.title,
		loc,
		img:
			apiProject.projectImages?.[0] ||
			apiProject.beforeImages?.[0] ||
			apiProject.afterImages?.[0] ||
			projectPlaceholder,
		stage: stageInfo.stage,
		stageTone: stageInfo.stageTone,
		config: configStr,
		style:
			apiProject.scopeDetails?.rooms?.join(", ") ||
			apiProject.scope ||
			"Full home",
		designer: designerName,
		studio: studio,
		designerIni: designerIni,
		designerTone: designerTone,
		proposalsCount: apiProject.proposalsCount,
		pct: stageInfo.pct,
		day: daysSinceCreated,
		days: totalDays,
		budget: apiProject.budgetMax || apiProject.budgetMin || 0,
		spent: 0,
		escrow: apiProject.totalFunded || 0,
		isPrimary: apiProject.isPrimary,
	};
}

function filterProjects(
	projects: readonly HoProject[],
	tab: FilterTab,
): readonly HoProject[] {
	switch (tab) {
		case "all":
			return projects;
		case "active":
			return projects.filter((p) => p.pct > 0 && p.pct < 100);
		case "awaiting":
			return projects.filter((p) => p.pct === 0 && !p.designer);
		case "done":
			return projects.filter((p) => p.pct === 100);
		default:
			return projects;
	}
}

export function HomeownerProjectsNew() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const filterParam = searchParams.get("filter") as FilterTab | null;
	const [filter, setFilter] = React.useState<FilterTab>(filterParam || "all");
	const accent = "var(--color-dashboard-accent)";
	const queryClient = useQueryClient();

	const { data: apiProjects = [], isLoading } = useProjects();

	const [makingPrimaryId, setMakingPrimaryId] = React.useState<string | null>(
		null,
	);
	const [isTransforming, setIsTransforming] = React.useState(false);
	const [transformedProjects, setTransformedProjects] = React.useState<
		HoProject[]
	>([]);

	React.useEffect(() => {
		if (apiProjects.length > 0) {
			setIsTransforming(true);
			Promise.all(apiProjects.map(transformProject))
				.then(setTransformedProjects)
				.finally(() => setIsTransforming(false));
		} else {
			setTransformedProjects([]);
			setIsTransforming(false);
		}
	}, [apiProjects]);

	const showLoading = isLoading || isTransforming;

	React.useEffect(() => {
		if (filterParam && FILTERS.some(([v]) => v === filterParam)) {
			setFilter(filterParam as FilterTab);
		}
	}, [filterParam]);

	const handleFilterChange = (newFilter: FilterTab) => {
		setFilter(newFilter);
		setSearchParams({ filter: newFilter });
	};

	const shown = filterProjects(transformedProjects, filter);

	const handleProjectOpen = (id: string) => {
		navigate(`/projects/${id}`);
	};

	const handleStartNewProject = () => {
		navigate("/projects/new");
	};

	async function handleMakePrimary(projectId: string) {
		setMakingPrimaryId(projectId);
		const result = await setPrimaryProject(projectId);
		setMakingPrimaryId(null);
		if (result.success) {
			queryClient.invalidateQueries({
				queryKey: queryKeys.projects.lists(),
			});
			toast.success("Primary project set successfully");
		} else {
			toast.error(result.error || "Failed to set primary project");
		}
	}

	const projectCount = transformedProjects.length;

	return (
		<div style={hoStyles.page}>
			<section style={hoStyles.head}>
				<div>
					<Eyebrow style={{ fontSize: 10, letterSpacing: "0.22em" }}>
						MY PROJECTS · {showLoading ? "..." : projectCount}
					</Eyebrow>
					<h1 style={hoStyles.title}>
						Every home,{" "}
						<ItalicAccent size={48} color={accent}>
							in view
						</ItalicAccent>
						.
					</h1>
				</div>
				<Btn
					variant="primary"
					size="md"
					icon={
						<Icn name="plus" color="var(--color-dashboard-on-fill)" size={16} />
					}
					onClick={handleStartNewProject}
				>
					Start a project
				</Btn>
			</section>

			<section style={hoStyles.section}>
				<div
					style={{
						display: "flex",
						gap: 10,
						marginBottom: 22,
						flexWrap: "wrap",
					}}
				>
					{FILTERS.map(([v, l]) => (
						<Chip
							key={v}
							active={filter === v}
							onClick={() => handleFilterChange(v)}
						>
							{l}
						</Chip>
					))}
				</div>

				{showLoading ? (
					<SkeletonRows
						label="Loading projects..."
						className="py-12"
						rowClassName="h-24 rounded-xl"
					/>
				) : (
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(3, 1fr)",
							gap: 18,
						}}
					>
						{shown.map((p) => (
							<HoProjectCard
								key={p.id}
								project={p}
								accent={accent}
								onOpen={() => handleProjectOpen(p.id)}
								makingPrimary={makingPrimaryId === p.id}
								onMakePrimary={() => handleMakePrimary(p.id)}
							/>
						))}
						{shown.length === 0 && (
							<div
								style={{
									gridColumn: "1/-1",
									padding: "48px 0",
									textAlign: "center",
									fontFamily: "var(--font-body)",
									fontSize: 14,
									color: "var(--color-dashboard-muted)",
								}}
							>
								No projects in this view.
							</div>
						)}
					</div>
				)}
			</section>

			<div style={{ height: 60 }} />
		</div>
	);
}
